import { useState, useEffect, useRef } from "react";
import axiosInstance from "@/config/axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { MessageCircle, Send, X, Loader2, User, ThumbsUp, ThumbsDown, Upload, Calendar } from "lucide-react";
import axios from "axios";
import AppointmentModal from "./AppointmentModal";

// Import pusherClient with error handling
let pusherClient;
try {
  const websocketModule = require("@/config/websocket");
  pusherClient = websocketModule.default || websocketModule;
  if (!pusherClient) {
    console.warn('[ChatWindow] ⚠️ pusherClient is null/undefined from websocket module');
  }
} catch (e) {
  console.warn('[ChatWindow] ⚠️ Failed to import websocket module:', e.message);
  pusherClient = null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session expiry: 30 days
const SESSION_EXPIRY_DAYS = 1;

export default function ChatWindow({ actorId, actorName, actorImage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('initial');
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [remainingMessages, setRemainingMessages] = useState(10);
  const [feedbackGiven, setFeedbackGiven] = useState({}); // Track feedback per message
  const [hasUploadedEvidence, setHasUploadedEvidence] = useState(false);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [evidenceSummary, setEvidenceSummary] = useState("");
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  
  // Sync ref with state
  useEffect(() => {
    appointmentModalOpenRef.current = appointmentModalOpen;
  }, [appointmentModalOpen]);
  
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const autoSendTimeoutRef = useRef(null);
  const sendingRef = useRef(false);
  const remainingMessagesRef = useRef(10);
  const appointmentModalOpenRef = useRef(false);
  
  // Sync refs with state whenever state changes
  useEffect(() => {
    sendingRef.current = sending;
  }, [sending]);
  
  useEffect(() => {
    remainingMessagesRef.current = remainingMessages;
  }, [remainingMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check for existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, [actorId]);

  // Listen for appointment message sent event
  useEffect(() => {
    const handleAppointmentMessageSent = (event) => {
      console.log('%c[ChatWindow] 📨 Received appointment-message-sent event:', 'color: #4CAF50; font-weight: bold', event.detail);
      
      const { appointmentData, sessionId: eventSessionId, actorId: eventActorId, message, aiResponse } = event.detail;
      
      console.log('[ChatWindow] 🔍 Checking conditions:', {
        eventSessionId,
        currentSessionId: sessionId,
        eventActorId,
        currentActorId: actorId,
        step,
        sessionMatch: eventSessionId === sessionId,
        actorMatch: eventActorId === actorId,
        isChatStep: step === 'chat'
      });
      
      // Only process if session_id matches current session
      if (eventSessionId === sessionId && eventActorId === actorId && step === 'chat') {
        console.log('%c[ChatWindow] ✅ Conditions met, adding messages to chat', 'color: #4CAF50; font-weight: bold');
        
        // Add formatted appointment message as user message
        setMessages(prev => {
          const newMessages = [...prev, {
            role: "user",
            content: message,
            timestamp: new Date().toISOString()
          }];
          console.log('[ChatWindow] 📝 Updated messages (user):', newMessages.length);
          return newMessages;
        });
        
        // Add AI response if available
        if (aiResponse) {
          setTimeout(() => {
            setMessages(prev => {
              const newMessages = [...prev, {
                role: "assistant",
                content: aiResponse,
                timestamp: new Date().toISOString()
              }];
              console.log('[ChatWindow] 📝 Updated messages (assistant):', newMessages.length);
              return newMessages;
            });
          }, 500); // Small delay to show user message first
        }
      } else {
        console.warn('[ChatWindow] ⚠️ Conditions not met, skipping:', {
          sessionMatch: eventSessionId === sessionId,
          actorMatch: eventActorId === actorId,
          isChatStep: step === 'chat'
        });
      }
    };

    console.log('[ChatWindow] 🎧 Setting up appointment-message-sent listener');
    window.addEventListener('appointment-message-sent', handleAppointmentMessageSent);
    
    return () => {
      console.log('[ChatWindow] 🧹 Cleaning up appointment-message-sent listener');
      window.removeEventListener('appointment-message-sent', handleAppointmentMessageSent);
    };
  }, [sessionId, actorId, step]);

  // WebSocket subscription for PublicAppointmentCreated channel - ONLY in ChatWindow
  useEffect(() => {
    // Only subscribe when chat is active (step === 'chat')
    if (step !== 'chat' || !sessionId || !actorId) {
      console.log('[ChatWindow] ⏸️ Skipping WebSocket subscription - chat not active');
      return;
    }

    // Check if pusherClient is available
    if (!pusherClient) {
      console.warn('%c[ChatWindow] ⚠️ Pusher client is not available!', 'color: #FF9800; font-weight: bold');
      console.warn('[ChatWindow] WebSocket features will be disabled. Make sure pusher-js is installed.');
      return;
    }

    console.log('%c[ChatWindow] 🚀 Initializing WebSocket subscription to PublicAppointmentCreated channel...', 'color: #4CAF50; font-weight: bold');
    console.log('[ChatWindow] 📋 Channel name: PublicAppointmentCreated');
    console.log('[ChatWindow] 🔍 Session ID:', sessionId);
    console.log('[ChatWindow] 🔍 Actor ID:', actorId);
    console.log('[ChatWindow] 🔍 Pusher client:', pusherClient);
    
    try {
      // Subscribe to public channel using Pusher JS directly
      const channel = pusherClient.subscribe('PublicAppointmentCreated');
      
      console.log('[ChatWindow] 📡 Channel object:', {
        name: channel.name,
        subscribed: channel.subscribed,
      });

      // Listen for subscription success
      channel.bind('pusher:subscription_succeeded', () => {
        console.log('%c[ChatWindow] ✅ Successfully subscribed to PublicAppointmentCreated channel', 'color: #4CAF50; font-weight: bold');
      });

      // Helper function to process appointment data
      const processAppointmentData = (appointmentData, eventName) => {
        console.log('%c[ChatWindow] 🔄 Processing appointment data:', 'color: #FF9800; font-weight: bold', appointmentData);
        
        // Check if session_id exists and match with current session
        const appointmentSessionId = appointmentData.session_id || appointmentData.sessionId;
        
        console.log('[ChatWindow] 🔍 Checking session_id match:', {
          appointmentSessionId,
          currentSessionId: sessionId,
          match: appointmentSessionId === sessionId
        });
        
        if (!appointmentSessionId || appointmentSessionId !== sessionId) {
          console.log('[ChatWindow] ⚠️ Session ID does not match, skipping...');
          return;
        }
        
        // Close appointment modal if it's open
        if (appointmentModalOpenRef.current) {
          console.log('[ChatWindow] 🔒 Closing appointment modal after receiving appointment event');
          setAppointmentModalOpen(false);
        }
        
        // Format appointment message
        const formatTime = (timeStr) => {
          if (!timeStr) return 'N/A';
          if (timeStr.length === 5) {
            return `${timeStr}:00`;
          }
          return timeStr;
        };
        
        const formatDate = (dateStr) => {
          if (!dateStr) return 'N/A';
          return dateStr;
        };
        
        // Extract data from flat structure (not nested)
        const clientName = appointmentData.clientName || appointmentData.client?.name || appointmentData.name || 'N/A';
        const clientEmail = appointmentData.clientEmail || appointmentData.client?.email || 'N/A';
        const clientPhone = appointmentData.clientPhone || appointmentData.client?.phone || appointmentData.phone || 'N/A';
        const serviceName = appointmentData.serviceName || appointmentData.service?.name || 'N/A';
        const requestedDate = appointmentData.requestedDate || appointmentData.requested_date || appointmentData.date || 'N/A';
        const requestedTime = appointmentData.requestedTime || appointmentData.requested_time || appointmentData.time || 'N/A';
        const duration = appointmentData.duration || 60;
        const status = appointmentData.status || 'pending';
        const priority = appointmentData.priority || 'high';
        
        // Extract checklist items from various possible field names
        let checklistItems = [];
        console.log('[ChatWindow] 🔍 Checking for checklist items in appointment data:', {
          checklistItems: appointmentData.checklistItems,
          checklist_items: appointmentData.checklist_items,
          checklist: appointmentData.checklist,
          items: appointmentData.items,
          allKeys: Object.keys(appointmentData)
        });
        
        if (appointmentData.checklistItems && Array.isArray(appointmentData.checklistItems)) {
          checklistItems = appointmentData.checklistItems;
        } else if (appointmentData.checklist_items && Array.isArray(appointmentData.checklist_items)) {
          checklistItems = appointmentData.checklist_items;
        } else if (appointmentData.checklist && Array.isArray(appointmentData.checklist)) {
          checklistItems = appointmentData.checklist;
        } else if (appointmentData.items && Array.isArray(appointmentData.items)) {
          checklistItems = appointmentData.items;
        }
        
        console.log('[ChatWindow] ✅ Extracted checklist items:', checklistItems);
        
        // Format checklist items as bullet points with proper spacing
        let checklistSection = '';
        if (checklistItems.length > 0) {
          // Format with proper newlines for markdown rendering
          checklistSection = `\nChecklist Items :\n${checklistItems.map(item => `- ${item}`).join('\n')}`;
        }
        
        // Build appointment message with proper newline spacing
        let appointmentMessage = `I have create an appointment, here is the details:\n
--------------------------------\n
\nName : ${clientName}
\nEmail : ${clientEmail}
\nPhone : ${clientPhone}
\nService : ${serviceName}`;

        // Add checklist items if available
        if (checklistSection) {
          appointmentMessage += checklistSection;
        }

        // Add newline before next section if checklist exists
        appointmentMessage += `\n\nRequested Date : ${formatDate(requestedDate)}
\nRequested Time : ${formatTime(requestedTime)}
\nDuration : ${duration}
\nStatus : ${status}
\nPriority : ${priority}
\n--------------------------------`;
        
        console.log('[ChatWindow] 📤 Formatted appointment message:', appointmentMessage);
        
        // Set formatted message to textarea
        setNewMessage(appointmentMessage);

        // Clear any existing timeout
        if (autoSendTimeoutRef.current) {
          clearTimeout(autoSendTimeoutRef.current);
        }

        // Auto-trigger send message immediately (smooth experience)
        // Use a small delay to ensure state is updated
        autoSendTimeoutRef.current = setTimeout(async () => {
          // Read current state values from refs (always up-to-date)
          const currentSending = sendingRef.current;
          const currentRemaining = remainingMessagesRef.current;
          
          // Check if we can send
          const canSend = appointmentMessage.trim() && !currentSending && currentRemaining > 0;
          
          if (canSend) {
            console.log('[ChatWindow] 🚀 Auto-sending appointment message...');
            
            // Clear textarea
            setNewMessage("");
            setSending(true);
            sendingRef.current = true;
            
            const userMessage = appointmentMessage.trim();
            
            // Add user message to UI immediately
            setMessages(prev => [...prev, {
              role: "user",
              content: userMessage,
              timestamp: new Date().toISOString()
            }]);
            
            try {
              const response = await axiosInstance.post(`${API}/public/chat/message`, {
                session_id: sessionId || "",
                message: userMessage,
                actor_id: actorId,
                visitor_name: name || '',
                visitor_phone: phone || ''
              });
              
              // Update session ID if backend created a new one
              if (response.data.session_id && response.data.session_id !== sessionId) {
                setSessionId(response.data.session_id);
                
                const storageKey = `chat_session_${actorId}`;
                const savedSession = localStorage.getItem(storageKey);
                if (savedSession) {
                  const sessionData = JSON.parse(savedSession);
                  sessionData.sessionId = response.data.session_id;
                  localStorage.setItem(storageKey, JSON.stringify(sessionData));
                }
              }
              
              // Add assistant response
              const assistantMsg = {
                role: "assistant",
                content: response.data.message,
                message_id: response.data.message_id,
                timestamp: new Date().toISOString(),
              };
              if (response.data.actions?.length) {
                assistantMsg.actions = response.data.actions;
              }
              setMessages((prev) => [...prev, assistantMsg]);
              
              setRemainingMessages(response.data.remaining_messages);
              remainingMessagesRef.current = response.data.remaining_messages;
              console.log('[ChatWindow] ✅ Appointment message sent successfully');
            } catch (error) {
              console.error('[ChatWindow] ❌ Failed to send appointment message:', error);
              toast.error(error.response?.data?.detail || 'Failed to send appointment message');
              // Remove the optimistic user message on error
              setMessages(prev => prev.slice(0, -1));
              setNewMessage(userMessage); // Restore the message
            } finally {
              setSending(false);
              sendingRef.current = false;
            }
          } else {
            console.warn('[ChatWindow] ⚠️ Cannot auto-send:', {
              hasMessage: !!appointmentMessage.trim(),
              sending: currentSending,
              remainingMessages: currentRemaining
            });
            toast.warning('Could not auto-send appointment message', {
              description: 'Please send manually',
            });
          }
        }, 100); // Small delay (100ms) to ensure state is updated smoothly
      };

      // Listen for ALL events on this channel
      channel.bind_global(async (eventName, data) => {
        console.log(`%c[ChatWindow] 📨 [GLOBAL] Event received on channel "${channel.name}":`, 'color: #2196F3; font-weight: bold', {
          eventName,
          data,
          dataType: typeof data,
          dataKeys: data ? Object.keys(data) : [],
          timestamp: new Date().toISOString(),
        });
        
        // Try to extract appointment data from various possible structures
        let appointmentData = null;
        
        if (data?.appointment) {
          appointmentData = data.appointment;
        } else if (data?.data?.appointment) {
          appointmentData = data.data.appointment;
        } else if (data?.data) {
          appointmentData = data.data;
        } else if (data && typeof data === 'object') {
          // Data is already flat structure
          appointmentData = data;
        }
        
        console.log('[ChatWindow] 📦 Extracted appointment data:', appointmentData);
        console.log('[ChatWindow] 📦 Data keys:', appointmentData ? Object.keys(appointmentData) : 'null');
        
        // Check if this looks like appointment data (has appointmentId or session_id)
        const isAppointmentData = appointmentData && (
          appointmentData.appointmentId ||
          appointmentData.session_id ||
          appointmentData.clientName ||
          appointmentData.serviceName ||
          appointmentData.requestedDate
        );
        
        // Process if we have valid appointment data
        if (isAppointmentData) {
          console.log('[ChatWindow] 🎯 Processing appointment event...');
          processAppointmentData(appointmentData, eventName);
        } else {
          console.log('[ChatWindow] ⚠️ Data does not look like appointment data, skipping...');
        }
      });

      // Handle subscription errors
      channel.bind('pusher:subscription_error', (error) => {
        console.error('%c[ChatWindow] ❌ Subscription error:', 'color: #F44336; font-weight: bold', error);
      });

      // Cleanup on unmount or when step changes
      return () => {
        console.log('%c[ChatWindow] 🧹 Cleaning up WebSocket subscription...', 'color: #9E9E9E; font-weight: bold');
        try {
          // Clear any pending auto-send timeout
          if (autoSendTimeoutRef.current) {
            clearTimeout(autoSendTimeoutRef.current);
            autoSendTimeoutRef.current = null;
          }
          
          channel.unbind_global();
          channel.unbind('pusher:subscription_succeeded');
          channel.unbind('pusher:subscription_error');
          pusherClient.unsubscribe('PublicAppointmentCreated');
          console.log('[ChatWindow] ✅ Cleanup completed');
        } catch (err) {
          console.warn('[ChatWindow] ⚠️ Error during cleanup:', err);
        }
      };
    } catch (error) {
      console.error('%c[ChatWindow] ❌ Failed to initialize WebSocket:', 'color: #F44336; font-weight: bold', error);
    }
  }, [step, sessionId, actorId, name, phone]);

  // Check upload status when session is ready
  const checkUploadStatus = async () => {
    if (!sessionId || !actorId) return;
    try {
      const res = await axiosInstance.get(
        `${API}/public/chat/upload-status?session_id=${sessionId}&actor_id=${actorId}`
      );
      setHasUploadedEvidence(res.data?.has_uploaded ?? false);
      setEvidenceSummary(res.data?.summary || "");
    } catch (e) {
      console.warn("Could not check upload status:", e);
    }
  };

  useEffect(() => {
    if (sessionId && actorId) checkUploadStatus();
  }, [sessionId, actorId]);

  // Auto-focus input when chat is ready and after sending message
  useEffect(() => {
    if (step === "chat" && !sending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [step, sending, messages]);

  const checkExistingSession = async () => {
    try {
      const storageKey = `chat_session_${actorId}`;
      const savedSession = localStorage.getItem(storageKey);
      
      if (savedSession) {
        const { sessionId, name, phone, expiryDate } = JSON.parse(savedSession);
        
        // Check if session is still valid (within 30 days)
        const now = new Date().getTime();
        const expiry = new Date(expiryDate).getTime();
        
        if (now < expiry) {
          // Session is still valid, restore it
          setSessionId(sessionId);
          setName(name);
          setPhone(phone || "");
          setStep("chat");
          
          // Add welcome back message
          setMessages([
            {
              role: "assistant",
              content: `Welcome back, ${name}! I'm ${actorName}. How can I help you today?`,
              timestamp: new Date().toISOString()
            }
          ]);
        } else {
          // Session expired, clear it
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.error("Error checking existing session:", error);
    }
  };

  const startChat = async () => {
    if (!name || !phone) {
      toast.error("Please enter your name and phone number");
      return;
    }

    setLoading(true);
    try {
      // Call API to initialize chat session
      const response = await axiosInstance.post(`${API}/public/chat/message`, {
        session_id: null,
        actor_id: actorId,
        visitor_name: name,
        visitor_phone: phone,
        message: "" // Empty message to initialize session
      });
      
      // Get session_id from response
      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      
      // Save session to localStorage with 30-day expiry
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + SESSION_EXPIRY_DAYS);

      const storageKey = `chat_session_${actorId}`;
      localStorage.setItem(storageKey, JSON.stringify({
        sessionId: newSessionId,
        name: name,
        phone: phone,
        expiryDate: expiryDate.toISOString()
      }));
      
      setStep("chat");
      
      // Add welcome message if provided in response, otherwise use default
      const welcomeMessage = response.data.message || `Hi ${name}! I'm ${actorName}. How can I help you today?`;
      setMessages([
        {
          role: "assistant",
          content: welcomeMessage,
          timestamp: new Date().toISOString()
        }
      ]);
      
      // Update remaining messages if provided
      if (response.data.remaining_messages !== undefined) {
        setRemainingMessages(response.data.remaining_messages);
        remainingMessagesRef.current = response.data.remaining_messages;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start chat");
      console.error("Error starting chat:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (messageToSend = null) => {
    // Allow passing message directly, or use state
    const messageText = messageToSend || newMessage;
    
    // Ensure messageText is a string
    if (!messageText || typeof messageText !== 'string') {
      console.warn('[ChatWindow] ⚠️ Invalid message text:', messageText);
      return;
    }
    
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage) return;

    const userMessage = trimmedMessage;
    setNewMessage("");
    setSending(true);
    sendingRef.current = true;

    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    let isConnectionLost = false;

    try {
      const response = await axiosInstance.post(`${API}/public/chat/message`, {
        session_id: sessionId || "",
        message: userMessage,
        actor_id: actorId,
        visitor_name: name,
        visitor_phone: phone
      });
      
      // Update session ID if backend created a new one
      if (response.data.session_id && response.data.session_id !== sessionId) {
        setSessionId(response.data.session_id);
        
        // Update localStorage with new session ID
        const storageKey = `chat_session_${actorId}`;
        const savedSession = localStorage.getItem(storageKey);
        if (savedSession) {
          const sessionData = JSON.parse(savedSession);
          sessionData.sessionId = response.data.session_id;
          localStorage.setItem(storageKey, JSON.stringify(sessionData));
        }
      }

      // Add assistant response with message ID and actions (for incident flow)
      const assistantMsg = {
        role: "assistant",
        content: response.data.message,
        message_id: response.data.message_id,
        timestamp: new Date().toISOString(),
      };
      if (response.data.actions?.length) {
        assistantMsg.actions = response.data.actions;
      }
      setMessages((prev) => [...prev, assistantMsg]);

      setRemainingMessages(response.data.remaining_messages);
      remainingMessagesRef.current = response.data.remaining_messages;
    } catch (error) {
      const errorDetail = error.response?.data?.detail || "Failed to send message";
      
      // Check if it's a connection lost error
      if (errorDetail === "CONNECTION_LOST" || error.response?.status === 503) {
        isConnectionLost = true;
        toast.error("Connection lost. Please try again later.");
        // Disable chat
        setMessages(prev => [...prev, {
          role: "system",
          content: "⚠️ Connection lost. The chat has been disconnected. Please refresh the page to start a new conversation.",
          timestamp: new Date().toISOString()
        }]);
        // Keep it disabled by NOT setting setSending(false)
      } else {
        toast.error(errorDetail);
        // Remove the optimistic user message on error
        setMessages(prev => prev.slice(0, -1));
        setNewMessage(userMessage); // Restore the message
      }
    } finally {
      // Only re-enable sending if it wasn't a connection lost error
      if (!isConnectionLost) {
        setSending(false);
        sendingRef.current = false;
      }
      
      // Keep focus on input after sending
      if (inputRef.current && !isConnectionLost) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    if (!files.length) return;
    if (!sessionId || !actorId) {
      toast.error("Session tidak valid");
      return;
    }
    if (files.length < 1 || files.length > 2) {
      toast.error("Upload 1-2 files");
      return;
    }
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg", "image/webp"];
    const invalid = files.find((f) => !allowedTypes.includes(f.type));
    if (invalid) {
      toast.error(`Format tidak didukung: ${invalid.name}`);
      return;
    }
    const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10MB total
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      toast.error(`Total size semua file maksimal 10MB (saat ini: ${(totalSize / 1024 / 1024).toFixed(2)}MB)`);
      return;
    }
    setUploadingEvidence(true);
    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      formData.append("actor_id", actorId);
      files.forEach((f) => formData.append("files", f));
      const res = await axiosInstance.post(`${API}/public/chat/upload-evidence`, formData);
      setHasUploadedEvidence(true);
      setEvidenceSummary(res.data?.summary || "");
      toast.success("Documents uploaded and analyzed successfully");
      // Add summary as assistant message with Schedule Appointment button
      if (res.data?.summary) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `${res.data.summary}\n\nYou can now schedule an appointment.`,
            timestamp: new Date().toISOString(),
            actions: [
              { type: "schedule_appointment", step: 2, label: "Schedule Appointment" },
            ],
          },
        ]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal upload");
    } finally {
      setUploadingEvidence(false);
    }
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const handleScheduleAppointment = () => {
    if (!hasUploadedEvidence) {
      toast.error("Silakan upload bukti terlebih dahulu sebelum menjadwalkan appointment");
      return;
    }
    setAppointmentModalOpen(true);
  };

  const submitFeedback = async (messageId, feedbackType) => {
    try {
      await axiosInstance.post(`${API}/public/chat/feedback`, {
        session_id: sessionId,
        actor_id: actorId,
        message_id: messageId,
        feedback_type: feedbackType,
        feedback_comment: null
      });
      
      setFeedbackGiven(prev => ({ ...prev, [messageId]: feedbackType }));
      toast.success(`Thank you for your feedback!`);
    } catch (error) {
      toast.error("Failed to submit feedback");
      console.error(error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (step === "initial") {
        startChat();
      } else {
        sendMessage();
      }
    }
  };

  return (
    <>
      {/* Floating Chat Button with Actor Image */}
      {!isOpen && (
        <div className="fixed bottom-24 right-6 z-[9998]">
          <button
            onClick={() => setIsOpen(true)}
            className="relative group"
          >
            {/* Actor Profile Image */}
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-2xl hover:scale-110 transition-transform duration-200 bg-slate-900">
              {actorImage ? (
                <img
                  src={`${API}/ai/actors/images/${actorImage}`}
                  alt={actorName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-900">
                  <User className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
            
            {/* Pulsing indicator */}
            <div className="absolute -top-1 -right-1">
              <div className="relative">
                <div className="w-5 h-5 bg-emerald-500 rounded-full border-2 border-white"></div>
                <div className="absolute inset-0 w-5 h-5 bg-emerald-500 rounded-full animate-ping"></div>
              </div>
            </div>
            
            {/* Chat message icon */}
            <div className="absolute -bottom-2 -right-2 bg-slate-900 rounded-full p-2 shadow-lg">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            
            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Chat with {actorName}
              <div className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
            </div>
          </button>
        </div>
      )}

      {/* Chat Window - Higher z-index to be above "Made with Emergent" badge */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-[10000] w-[calc(100vw-3rem)] h-[calc(100vh-8rem)] md:bottom-0 md:right-0 md:top-0 md:w-[50vw] md:h-screen max-w-full">
          <Card className="shadow-2xl border-2 h-full flex flex-col">
            <CardHeader className="bg-slate-900 text-white rounded-t-lg md:rounded-none flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Chat with {actorName}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-slate-800"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-0 flex-1 flex flex-col overflow-hidden">
              {step === "initial" && (
                <div className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Enter your details to start chatting.
                  </p>
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Your phone number"
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <Button
                    onClick={startChat}
                    disabled={loading || !name || !phone}
                    className="w-full bg-slate-900 hover:bg-slate-800"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start Chat"}
                  </Button>
                </div>
              )}

              {step === "chat" && (
                <>
                  {/* Hidden file input for evidence upload - single instance, multiple files */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                    {messages.map((msg, index) => (
                      <div
                        key={index}
                        className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            msg.role === "user"
                              ? "bg-slate-900 text-white"
                              : "bg-white border border-slate-200"
                          }`}
                        >
                          <div className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-p:leading-relaxed prose-pre:my-2 prose-pre:bg-slate-100 prose-pre:p-2 prose-pre:rounded prose-pre:overflow-x-auto">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                p: ({ node, ...props }) => <p className="whitespace-pre-wrap break-words mb-2 last:mb-0" {...props} />,
                                br: () => <br className="block" />,
                                li: ({ node, ...props }) => <li className="my-0.5 leading-relaxed" {...props} />,
                                ul: ({ node, ...props }) => <ul className="my-2 ml-4 list-disc space-y-1" {...props} />,
                                ol: ({ node, ...props }) => <ol className="my-2 ml-4 list-decimal space-y-1" {...props} />,
                              }}
                            >
                              {msg.content}
                            </ReactMarkdown>
                          </div>
                        </div>
                        {/* Action Buttons (Upload Evidence, Schedule Appointment) */}
                        {msg.role === "assistant" && msg.actions?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3 ml-2">
                            {msg.actions.map((action, idx) => {
                              const isUpload = action.type === "upload_evidence";
                              const isSchedule = action.type === "schedule_appointment";
                              const scheduleDisabled = isSchedule && !hasUploadedEvidence;
                              return (
                                <Button
                                  key={idx}
                                  variant={isUpload ? "default" : "outline"}
                                  size="sm"
                                  disabled={isSchedule && scheduleDisabled}
                                  onClick={() =>
                                    isUpload ? openFilePicker() : handleScheduleAppointment()
                                  }
                                  className={
                                    isUpload
                                      ? "bg-slate-900 hover:bg-slate-800"
                                      : scheduleDisabled
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }
                                >
                                  {isUpload ? (
                                    <>
                                      <Upload className="w-3 h-3 mr-1" />
                                      {uploadingEvidence ? "Uploading..." : (action.label || "Upload Bukti (1-2 files, max 10MB)")}
                                    </>
                                  ) : (
                                    <>
                                      <Calendar className="w-3 h-3 mr-1" />
                                      {action.label || "Jadwalkan Appointment"}
                                    </>
                                  )}
                                </Button>
                              );
                            })}
                          </div>
                        )}
                        {/* Feedback Buttons for Assistant Messages */}
                        {msg.role === "assistant" && msg.message_id && (
                          <div className="flex items-center gap-2 mt-2 ml-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => submitFeedback(msg.message_id, "positive")}
                              disabled={feedbackGiven[msg.message_id]}
                              className={`h-7 px-2 ${
                                feedbackGiven[msg.message_id] === "positive"
                                  ? "text-green-600 bg-green-50"
                                  : "text-slate-400 hover:text-green-600 hover:bg-green-50"
                              }`}
                            >
                              <ThumbsUp className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => submitFeedback(msg.message_id, "negative")}
                              disabled={feedbackGiven[msg.message_id]}
                              className={`h-7 px-2 ${
                                feedbackGiven[msg.message_id] === "negative"
                                  ? "text-red-600 bg-red-50"
                                  : "text-slate-400 hover:text-red-600 hover:bg-red-50"
                              }`}
                            >
                              <ThumbsDown className="w-3 h-3" />
                            </Button>
                            {feedbackGiven[msg.message_id] && (
                              <span className="text-xs text-muted-foreground">Thank you!</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {sending && (
                      <div className="flex justify-start">
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="p-4 border-t flex-shrink-0">
                    <div className="flex items-end gap-2">
                      <Textarea
                        ref={inputRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (!sending && newMessage && typeof newMessage === 'string' && newMessage.trim() && remainingMessages > 0) {
                              sendMessage();
                            }
                          }
                        }}
                        disabled={sending || remainingMessages <= 0}
                        className="resize-none min-h-[44px] max-h-[120px]"
                        rows={1}
                        autoFocus
                      />
                      <Button
                        onClick={() => sendMessage()}
                        disabled={sending || !newMessage || (typeof newMessage === 'string' && !newMessage.trim()) || remainingMessages <= 0}
                        className="bg-slate-900 hover:bg-slate-800 h-[44px]"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {remainingMessages} messages remaining today
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Appointment Modal */}
      <AppointmentModal
        open={appointmentModalOpen}
        onOpenChange={setAppointmentModalOpen}
        sessionId={sessionId}
        actorId={actorId}
        userName={name}
        userPhone={phone}
      />
    </>
  );
}
